import React, { memo, useEffect } from "react"
import Box from "@mui/material/Box"
import { useInfoStore } from "../../../store/userInfo";
import { ButtonBase, Divider, IconButton, List, Typography, styled } from "@mui/material";
import { useOrderStore } from "../../../store/order";
import { ICart, IOrderModifiers } from "../../../types/menu";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { useTranslation } from "react-i18next";
import { useWindowDimensions } from "../../../App";
import { useRecommendDetail } from "./RecommendDetailContext";
import { useRecommendStore } from "../../../store/recommend";
import { logger } from "../../../utils/logger";
// import {TurnLeft} from '@mui/icons-material';

const LazyPicture = styled(LazyLoadImage as any)`
  object-fit: cover;
  width: ${({ width }: { width?: string }) => (width ? `${width}rem` : "100%")};
  height: ${({ width }: { width?: string }) => (width ? `${width}rem` : "100%")};
  border-radius: 1.5rem;
`;

const OrderBox = memo((props: {setOpenOrderBox: any; checkLimitText: any; selectRequired: any; setSelectRequired: any}) => {
  const { setOpenOrderBox, checkLimitText, selectRequired, setSelectRequired } = props;
  const { recommendItem, setRecommendItem, handleAddtoCart, handleAddCount: contextHandleAddCount, handleSubCount: contextHandleSubCount, handleClose } = useRecommendDetail();
  const { preRecommendOrder, setPreRecommendOrder } = useOrderStore();
  const { checkedModifierPrice } = useOrderStore();
  const { orientation } = useWindowDimensions();
  const { userInfo, tableInfo, currentLng } = useInfoStore();
  const { setOpenCart, setSnackMode, setShowSnackbar, setLimitValue } = useOrderStore();
  const storeId = userInfo?.storeId;
  const tableId = tableInfo.id;
  const isAyce = userInfo?.isAyce;
  const { t } = useTranslation(`${storeId}`);
  const landMode = (orientation === "landscape") ? true : false;
  const height = `calc(100% - 7rem)`;

  // selectRequired 상태 변화 추적
  useEffect(() => {
    logger.log('OrderBox - selectRequired changed to:', selectRequired);
    logger.log('OrderBox - Current recommendItem:', recommendItem);
    logger.log('OrderBox - Current preRecommendOrder:', preRecommendOrder);
  }, [selectRequired, recommendItem, preRecommendOrder]);

  const itemName = preRecommendOrder ? t(`itemNames.${preRecommendOrder.itemId}.${currentLng}`) : '';

  // Render nothing if preRecommendOrder is missing
  if (!preRecommendOrder) {
    return null;
  }

  const handleAddCount = (item: ICart) => {
    if (!recommendItem) return;

    const newQuantity = item.quantity + 1;
    const maxLimit = recommendItem.maxAllowed;

    if (maxLimit && newQuantity > maxLimit) {
      setLimitValue(maxLimit);
      setSnackMode(11);
      setShowSnackbar(true);
      return;
    }
    if (preRecommendOrder) {
      setPreRecommendOrder({...preRecommendOrder, quantity: item.quantity + 1});
    }
  };

  const handleSubCount = (item: ICart) => {
    if (!recommendItem) return;
    const newQuantity = item.quantity - 1;
    if (recommendItem.minRequired && newQuantity < recommendItem.minRequired) {
      setLimitValue(recommendItem.minRequired);
      setSnackMode(13);
      setShowSnackbar(true);
      return;
    }
    if (preRecommendOrder) {
      setPreRecommendOrder({...preRecommendOrder, quantity: newQuantity});
    }
  };

  const handleDeleteItem = (item: ICart): void => {
    if(!recommendItem) return;
    const updatedDetail = recommendItem.modifierGroups?.map((group) => ({
      ...group,
      modifiers: group.modifiers.map((modifier) => ({
        ...modifier,
        quantity: 0,
        modifiers: [],
      })),
    })) || [];
    setRecommendItem({...recommendItem, modifierGroups: updatedDetail});
    if (preRecommendOrder) {
      setPreRecommendOrder({ ...preRecommendOrder, quantity: 1, orderModifiers: [] });
    }
  };

  const handleConfirm = async (e: any): Promise<void> => {
    logger.log('OrderBox handleConfirm - selectRequired:', selectRequired);
    logger.log('OrderBox handleConfirm - recommendItem:', recommendItem);
    logger.log('OrderBox handleConfirm - preRecommendOrder.hasModifier:', preRecommendOrder.hasModifier);
    
    // 직접 modifier 상태를 확인하는 함수
    const checkAllRequiredSatisfied = (groups: any[]): boolean => {
      if (!groups || groups.length === 0) return true;

      for (const group of groups) {
        if (!group.modifiers) continue;

        // 현재 그룹의 선택된 수량 계산
        const currentGroupQuantity = group.modifiers.reduce(
          (sum: number, mod: any) => sum + (mod.quantity || 0),
          0
        );
    
        if (group.isRequired) {
          // 기본값: minRequired가 null이면 0, maxAllowed가 null이면 무제한으로 처리
          const minRequired = group.minRequired !== null ? group.minRequired : 0;
          const maxAllowed = group.maxAllowed !== null ? group.maxAllowed : Infinity;
    
          if (currentGroupQuantity < minRequired || currentGroupQuantity > maxAllowed) {
            return false;
          }
        }
  
        // 재귀적으로 하위 modifierGroup 체크
        for (const mod of group.modifiers) {
          if (mod.modifiers && mod.modifiers.length > 0) {
            if (!checkAllRequiredSatisfied(mod.modifiers)) {
              return false;
            }
          }
        }
      }
      return true;
    };

    // Optional modifier의 min 조건 체크 함수
    const checkOptionalMinRequirements = (groups: any[]): { isValid: boolean; minValue?: number; groupName?: string } => {
      for (const group of groups) {
        if (!group.modifiers) continue;

        // 현재 그룹의 선택된 수량 계산
        const currentGroupQuantity = group.modifiers.reduce(
          (sum: number, mod: any) => sum + (mod.quantity || 0),
          0
        );

        // Optional 그룹이지만 minRequired가 설정된 경우 체크
        // 단, 하나라도 선택된 경우에만 min 조건을 체크
        if (!group.isRequired && group.minRequired !== null && group.minRequired > 0 && currentGroupQuantity > 0) {
          if (currentGroupQuantity < group.minRequired) {
            return { 
              isValid: false, 
              minValue: group.minRequired,
              groupName: group.modifierGroupName 
            };
          }
        }

        // 재귀적으로 하위 modifierGroup 체크
        for (const mod of group.modifiers) {
          if (mod.modifiers && mod.modifiers.length > 0) {
            const result = checkOptionalMinRequirements(mod.modifiers);
            if (!result.isValid) {
              return result;
            }
          }
        }
      }
      return { isValid: true };
    };

    // 현재 상태를 직접 확인
    const currentSelectRequired = checkAllRequiredSatisfied(recommendItem?.modifierGroups || []);
    logger.log('OrderBox handleConfirm - currentSelectRequired:', currentSelectRequired);
    
    if(preRecommendOrder.hasModifier && !currentSelectRequired) {
      logger.log('require false')
      setSnackMode(0)
      setShowSnackbar(true)
      return; 
    }

    // Optional modifier의 min 조건 체크
    if (preRecommendOrder.hasModifier && recommendItem?.modifierGroups) {
      const optionalCheck = checkOptionalMinRequirements(recommendItem.modifierGroups);
      if (!optionalCheck.isValid) {
        // 새로운 snackMode 추가 (15번)
        setSnackMode(15);
        setLimitValue(optionalCheck.minValue || 0);
        setShowSnackbar(true);
        return;
      }
    }
    try {
      await useOrderStore.getState().setCart({ item: preRecommendOrder, type: 'add' });
      logger.log('Cart updated successfully');
      // 추가 작업 수행
      const { updateCart } = useOrderStore.getState();
      if (updateCart) {
        logger.log('detail add to cart');
        setSnackMode(14);
        setShowSnackbar(true);
        // 주문 성공 후 preRecommendOrder 초기화
        setPreRecommendOrder({
          ...preRecommendOrder,
          quantity: 1,
          orderModifiers: [],
          hasModifier: false,
          itemWithModifierPrice: preRecommendOrder.itemPrice
        });
      }
      // setOpenCart(true)
    } catch (error) {
      logger.error('Failed to update cart', error);
      // 오류 처리
    }

    // setSelectOptionals([])
    // checkedModifierPrice({price: 0, type: 'reset'});
    setSelectRequired(false);

    // 주문 완료 후 모든 상태 초기화
    handleClose();
  }

  const renderModifiers = (modifiers: IOrderModifiers[], depth = 0) => {
    return modifiers.map((modifier: IOrderModifiers, index: number) => (
      <React.Fragment key={`modifier-${modifier.modifierId}-${depth}-${index}`}>
      <Box>
        <Box
          sx={{
            paddingLeft: `${depth * 1.2}rem`, // Depth에 따라 들여쓰기
            // paddingY: "8rem",
            display: "flex",
            flexDirection: "column",
            // justifyContent: "space-between",
            justifyContent: 'flex-start',
            alignItems: "flex-start",
            // background: depth === 0 ? "#F0F0F0" : "transparent", // Depth에 따라 스타일 변경
            // borderRadius: depth === 0 ? "10rem" : "0rem",
            // padding: depth === 0 ? "13rem 12rem" : "0rem",
          }}
        >
          
          <Box sx={{ display: "flex",flexDirection: "row", width: '100%',}}>
            {/* {depth !== 0 && <TurnLeft sx={{transform: "rotate(180deg)", fontSize: '10rem'}}/>} */}
            <Typography
              sx={{
                fontSize: "1.6rem",
                fontWeight: "500",
                flex: 1,
                textAlign: "left",
                color: '#0085FF'
              }}
            >
              {modifier.modifierGroupName}
            </Typography>
            {/* {groupState(modifier.isRequired, modifier.path)} */}
          </Box>
          <Box sx={{ display: "flex",flexDirection: "row", width: '100%', paddingLeft: `${depth+1 * 1.2}rem`}}>
            <Typography
              sx={{
                fontSize: "1.6rem",
                fontWeight: "500",
                flex: 1,
                textAlign: "left",
              }}
            >
              {modifier.modifierName}
            </Typography>
            <Typography
              sx={{
                fontSize: "1.6rem",
                fontWeight: "600",
                color: "#F72E13",
                textAlign: "right",
                width: "4.0rem",
              }}
            >
              x{modifier.quantity}
            </Typography>
            <Typography
              sx={{
                fontSize: "1.6rem",
                fontWeight: "600",
                color: "#F72E13",
                textAlign: "right",
                width: "7.0rem",
              }}
            >
              ${(modifier.modifierPrice / 100).toFixed(2)}
            </Typography>
          </Box>
        </Box>

      </Box>
      {modifier.orderModifiers && modifier.orderModifiers.length > 0 && (
        <Box key={`nested-modifiers-${modifier.modifierId}-${depth}`} sx={{ width: "100%" }}>{renderModifiers(modifier.orderModifiers, depth + 1)}</Box>
      )}
      </React.Fragment>
    ));
  };

  return (
    <Box sx={{ width: '100%', height: height, background: '#fff', borderRadius: '1.5rem 1.5rem 0rem 0rem', overflow: 'hidden' }}>
      <List style={{ overflow: 'overlay', overflowX: 'hidden', marginTop: '.4rem', height: `calc(100vh - 32rem)` }}>
        <Box sx={{ justifyContent: 'center', mx: '2.5rem',}}>
          <Typography sx={{ fontSize: '2.1rem', fontWeight: '600', fontStyle: 'normal', letterSpacing: '-.02rem'}}>
            {itemName}
          </Typography>
          { preRecommendOrder?.orderModifiers && preRecommendOrder.orderModifiers.length !== 0 ?
            <Box sx={{background: '#F0F0F0', borderRadius: '1.0rem', padding: '1.3rem 1.2rem', alignItems: 'flex-start', gap: '1.0rem'}}>
              {renderModifiers(preRecommendOrder.orderModifiers)}
            </Box>
          :<></>}
          <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
            <Typography sx={{fontSize: '1.6rem', fontWeight: '500', fontStyle: 'normal', color: '#636773', letterSpacing: '-.016rem'}}>Regular Price</Typography>
            <Typography sx={{fontSize: '1.6rem', fontWeight: '500', fontStyle: 'normal', color: '#636773',letterSpacing: '-.016rem', width: '6.5rem', textAlign: 'end'}}>${(preRecommendOrder?.itemPrice/100).toFixed(2)}</Typography>
          </Box>
          <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
            <Typography sx={{fontSize: '1.6rem', fontWeight: '500', fontStyle: 'normal', color: '#636773', letterSpacing: '-.016rem'}}>Option Price</Typography>
            <Typography sx={{fontSize: '1.6rem', fontWeight: '500', fontStyle: 'normal', color: '#636773',letterSpacing: '-.016rem', width: '6.5rem', textAlign: 'end'}}>${((preRecommendOrder?.modifierPrice || 0)/100).toFixed(2)}</Typography>
          </Box>
          <Typography sx={{fontSize: '2.0rem', fontWeight: '600', letterSpacing:'-.02rem', textAlign: 'right', mt: '.5rem'}}>${((preRecommendOrder?.subtotalAmount || 0)/100).toFixed(2)}</Typography>
        </Box>
      </List>
      <Box>
        {(recommendItem?.maxAllowed || recommendItem?.minRequired! > 1) &&
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', mr: '1.0rem', alignItems: 'flex-end', height: '4.0rem'}}>
            <LazyPicture  style={{width: '2.4rem', height: '2.4rem'}}
              effect="opacity"
              src={process.env.PUBLIC_URL+'/assets/images/warning_r.svg'}
            />
            <Typography sx={{fontSize: '2.0rem', fontWeight: '600', color: '#F72E13'}}>
              {checkLimitText()}
            </Typography>
          </Box> 
        }
        <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', my: '.5rem', gap: '3.0rem'}}>
          <ButtonBase sx={{color:'#F72E13', borderRadius: '3.0rem', border: '2px solid #F72E13', backgroundColor: '#fff', width: '12.6rem', p: '.9rem 2.1rem', gap: '1.0rem', alignItems: 'center'}}
            onClick={() => handleDeleteItem(preRecommendOrder)}>
            <Typography sx={{color:'#F72E13', fontSize: '1.6rem', fontStyle: 'normal', fontWeight: '600', letterSpacing: '-.016rem'}}>Remove</Typography>
          </ButtonBase>
          <Box sx={{display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: '1.6rem', width: '13.4rem',
            padding: '.5rem .9rem', borderRadius: '3.0rem', backgroundColor: '#fff', border: '2px solid #E9E9E9'}}>
            <IconButton sx={{p : '0rem'}}
              aria-label="minus"
              onClick={() => handleSubCount(preRecommendOrder)}
            >
              <LazyPicture 
                style={{width: '2.4rem', height: '2.4rem'}}
                effect="opacity"
                src={process.env.PUBLIC_URL+'/assets/images/minus.svg'}
              />
            </IconButton>
            <Typography id="item-price" sx={{fontSize: '2.2rem', fontWeight: '700', fontStyle: 'normal', width: '4.5rem', textAlign: 'center'}}>
              {preRecommendOrder.quantity}
            </Typography>
            <IconButton  sx={{p : '0rem'}}
              aria-label="plus"
              onClick={() => handleAddCount(preRecommendOrder)}
            >
              <LazyPicture 
                style={{width: '2.4rem', height: '2.4rem'}}
                effect="opacity"
                src={process.env.PUBLIC_URL+'/assets/images/add.svg'}
                alt="add"
                loading="lazy"
              />
            </IconButton>
          </Box>
        </Box>
        <Box sx={{display: 'flex', flexDirection: 'column', mb: '1.3rem', rem: '2.0rem', justifyContent: 'center', alignItems: 'center', width: '100%'}}>
          <Divider sx={{display: 'flex', width: '100%', height: '2px', backgroundColor: '#000', mt: '.3rem'}}/>
        
          <Box sx={{display: 'flex', flexDirection: 'row', width: '100%', mt: '1.5rem', justifyContent: 'flex-end', alignItems: 'center'}}>
            <Typography sx={{fontSize: '2.2rem', fontWeight: '500', fontStyle: 'normal', letterSpacing: '-.022rem', lineHeight: '2.4rem'}}>Qty </Typography>
            <Typography sx={{fontSize: '2.4rem', fontWeight: '700', fontStyle: 'normal', width: '6.7rem', textAlign: 'end', lineHeight: '2.4rem', mr: '1.0rem' }}>{preRecommendOrder.quantity}</Typography>
          </Box>
          <Box sx={{width: '100%', mx: '1rem', mt: '1.5rem', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
            <Box sx={{display: 'flex',  width: '98%', flexDirection: 'row', padding: '.7rem .4rem', justifyContent: 'flex-end', height: '3.6rem', backgroundColor: '#FFD600'}}>
              <Typography sx={{fontSize: '2.2rem', fontWeight: '600', fontStyle: 'normal', letterSpacing: '-.022rem', lineHeight: '2.2rem', width: '10.2rem'}}>Subtotal </Typography>
              <Typography sx={{fontSize: '2.6rem', fontWeight: '700', fontStyle: 'normal', letterSpacing: '-.026rem', lineHeight: '2.2rem', width: '12.3rem', textAlign: 'end'}}>${(((preRecommendOrder?.subtotalAmount || 0) / 100) * (preRecommendOrder?.quantity || 0)).toFixed(2)}</Typography>
            </Box>
          </Box>
          <Box sx={{ justifyContent: 'center', alignItems: 'center', display: 'flex', my: '1.0rem', mx: '1rem', width: '100%' }}>
            <ButtonBase sx={{backgroundColor: '#F72E13', p: '1.0rem 2.0rem', borderRadius: '4.0rem', width: '98%'}} onClick={handleConfirm}>
              <Typography sx={{color: '#fff', fontSize: '20px', fontStyle: 'normal', fontWeight: '600', mb: 0}}>Confirm & Add to Cart</Typography>
            </ButtonBase>
          </Box>
        </Box>
      </Box> 
    </Box>
  )
});
export default OrderBox


